import styled from '@emotion/styled';
import { Fragment, useState, useMemo, Suspense } from 'react';
import { useLazyLoadQuery, graphql } from 'react-relay';
import { Link } from 'react-router-dom';

import type { FreeTextbooksSyllabusSelectorCurriculumSelectorQuery } from '__free_textbooks_generated__/FreeTextbooksSyllabusSelectorCurriculumSelectorQuery.graphql';
import ErrorBoundaryWithRetry from 'ms-components/ErrorBoundaryWithRetry';
import ChevronBottom from 'ms-components/icons/ChevronBottom';
import ChevronRight from 'ms-components/icons/ChevronRight';
import PageDescription from 'ms-helpers/PageDescription';
import PageTitle from 'ms-helpers/PageTitle';
import { useSnowplow } from 'ms-helpers/Snowplow/useSnowplow';
import type { CountryCode } from 'ms-pages/FreeTextbooks/types';
import {
  freeTextbooksRootSelector,
  getTextbookSelectorUrl,
} from 'ms-pages/FreeTextbooks/urls';
import MinorSpinner from 'ms-pages/Teacher/components/MinorSpinner';
import { font, fontFamily, fontSize, fontWeight } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import { BASE_UNIT } from 'ms-styles/theme/Numero';
import Button from 'ms-ui-primitives/Button';
import Separator from 'ms-ui-primitives/Separator';
import { multilineTextOverflow, tappable } from 'ms-utils/emotion';
import extractNode from 'ms-utils/relay/extractNode';

import * as DARK_THUMBS from './thumbnails/dark';
import {
  CurriculumItem,
  CurriculumDetails,
  CurriculumGridWrapper,
  Title as CurriculumTitle,
  Subtitle as CurriculumSubtitle,
  CurriculumThumbnailWrapper,
  CurriculumThumbnail,
} from '../FreeTextbooksSyllabusSelector/elements';
import seo from '../seo';

const Title = styled.h1({
  color: colors.cloudBurst,
  ...multilineTextOverflow(2),
  ...font.gilroyLargerHeading,
  ...tappable,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: 40,
  paddingBottom: 8,
});

const SubTitle = styled(Link)({
  display: 'block',
  fontFamily: fontFamily.body,
  fontSize: fontSize.medium,
  fontWeight: fontWeight.semibold,
  color: colors.eggplant,
  paddingBottom: 40,
  textDecoration: 'none',
});

const ChevronWrapper = styled.div({
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
});

const Centered = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: 47,
});

const query = graphql`
  query FreeTextbooksSyllabusSelectorCurriculumSelectorQuery(
    $countryCode: CountryCodeEnum
    $isInternational: Boolean!
  ) {
    curriculums(
      first: 1000
      countryCode: $countryCode
      isInternational: $isInternational
    ) {
      edges {
        node {
          id
          title
          countrySubdivision
          syllabuses {
            totalCount
          }
        }
      }
    }
  }
`;

const SUBDIVISIONS_THUMBNAIL_URL_MAP: Record<string, string> = {
  NSW: DARK_THUMBS.NSW,
  VIC: DARK_THUMBS.VIC,
  QLD: DARK_THUMBS.QLD,
  WA: DARK_THUMBS.WA,
  DEFAULT: DARK_THUMBS.DEFAULT,
};

function getCountrySubdivisionThumbnailImageUrl(countrySubdivision: string) {
  if (SUBDIVISIONS_THUMBNAIL_URL_MAP.hasOwnProperty(countrySubdivision)) {
    return SUBDIVISIONS_THUMBNAIL_URL_MAP[countrySubdivision];
  } else {
    return SUBDIVISIONS_THUMBNAIL_URL_MAP.DEFAULT;
  }
}

const MAX_CURRICULUMS_TO_SHOW = 12;
const MIN_CURRICULUMS_TO_REVEAL = 4;
const getNumberOfCurriculumsToShowImmediately = (
  numberOfCurriculums: number,
) =>
  numberOfCurriculums > MAX_CURRICULUMS_TO_SHOW + MIN_CURRICULUMS_TO_REVEAL
    ? MAX_CURRICULUMS_TO_SHOW
    : numberOfCurriculums;

type Props = { countryCode: CountryCode };

export default function FreeTextbooksSyllabusSelectorCurriculumSelector({
  countryCode,
}: Props) {
  return (
    <ErrorBoundaryWithRetry name="FreeTextbooksSyllabusSelectorCurriculumSelector">
      {({ fetchKey }) => (
        <Suspense fallback={<MinorSpinner />}>
          <FreeTextbooksSyllabusSelectorCurriculumSelectorInner
            countryCode={countryCode}
            fetchKey={fetchKey}
          />
        </Suspense>
      )}
    </ErrorBoundaryWithRetry>
  );
}

function FreeTextbooksSyllabusSelectorCurriculumSelectorInner({
  countryCode,
  fetchKey,
}: Props & { fetchKey: string }) {
  const data =
    useLazyLoadQuery<FreeTextbooksSyllabusSelectorCurriculumSelectorQuery>(
      query,
      {
        countryCode: countryCode !== 'international' ? countryCode : null,
        isInternational: countryCode === 'international',
      },
      {
        fetchKey,
        networkCacheConfig: {
          // @ts-expect-error TODO fix this once we upgrade relay to latest
          // The relay-hooks types seem suss
          useCache: true,
          ttl: Infinity,
        },
      },
    );

  const curriculums = extractNode(data.curriculums);

  const numberOfCurriculums = curriculums.length;
  const numberOfCurriculumsToShowImmediately =
    getNumberOfCurriculumsToShowImmediately(numberOfCurriculums);

  const [isCollapsed, setCollapsed] = useState(
    numberOfCurriculumsToShowImmediately < numberOfCurriculums,
  );

  const { trackStructEvent } = useSnowplow();

  const countryName = useMemo(() => getCountryName(countryCode), [countryCode]);
  return (
    <>
      <PageTitle subtitles={seo.CURRICULUM_SELECTOR.title(countryName)} />
      <PageDescription
        description={seo.CURRICULUM_SELECTOR.description(countryName)}
      />

      <Title>Available curriculums in {countryName}</Title>
      <SubTitle to={freeTextbooksRootSelector}>Select other countries</SubTitle>
      <CurriculumGridWrapper>
        {curriculums
          .slice(
            0,
            isCollapsed
              ? numberOfCurriculumsToShowImmediately
              : numberOfCurriculums,
          )
          .map(curriculum => (
            <Fragment key={curriculum.id}>
              <CurriculumItem
                to={getTextbookSelectorUrl({
                  countryCode,
                  curriculumId: curriculum.id,
                })}
                onClick={() => {
                  trackStructEvent({
                    category: 'free_textbook',
                    action: 'select_curriculum',
                    property: 'curriculumId',
                    label: curriculum.id,
                  });
                }}
              >
                <CurriculumThumbnailWrapper>
                  <CurriculumThumbnail
                    src={getCountrySubdivisionThumbnailImageUrl(
                      curriculum.countrySubdivision,
                    )}
                  />
                </CurriculumThumbnailWrapper>
                <CurriculumDetails>
                  <CurriculumTitle oneLine style={{ color: colors.eggplant }}>
                    {curriculum.title}
                  </CurriculumTitle>
                  <CurriculumSubtitle>
                    {curriculum.syllabuses.totalCount}{' '}
                    {curriculum.syllabuses.totalCount === 1
                      ? 'textbook'
                      : 'textbooks'}
                  </CurriculumSubtitle>
                </CurriculumDetails>
                <ChevronWrapper>
                  <ChevronRight size={4 * BASE_UNIT} color={colors.eggplant} />
                </ChevronWrapper>
              </CurriculumItem>
            </Fragment>
          ))}
      </CurriculumGridWrapper>
      {isCollapsed && (
        <Centered>
          <Button
            onClick={() => {
              setCollapsed(false);
            }}
            label="View all curriculums"
          >
            View all <Separator size={1} />{' '}
            <ChevronBottom size={4 * BASE_UNIT} />
          </Button>
        </Centered>
      )}
    </>
  );
}

function getCountryName(countryCode: CountryCode) {
  switch (countryCode) {
    case 'AU':
      return 'Australia';
    case 'US':
      return 'United States of America';
    case 'GB':
      return 'Great Britain';
    case 'IN':
      return 'India';
    case 'HK':
      return 'Hong Kong';
    case 'SG':
      return 'Singapore';
    case 'CA':
      return 'Canada';
    case 'NZ':
      return 'New Zealand';
    case 'international':
      return 'International';
    default:
      return countryCode;
  }
}
